import { Ionicons } from '@expo/vector-icons';
import { router } from 'expo-router';
import React, { useMemo, useState } from 'react';
import { View, TouchableOpacity, Pressable, ActivityIndicator, Linking, ViewStyle, Platform } from 'react-native';

import { GlassCard } from '@/components/common/glass-card';
import { TaskDetailModal } from '@/components/tasks/task-detail-modal';
import { Text, useTheme } from '@/components/ui';
import { useApi } from '@/hooks/use-api';
import { useDashboardTasks } from '@/hooks/use-dashboard-tasks';
import { useTasks, useCalendarEvents } from '@/hooks/use-data';
import { useFeatureFlags } from '@/hooks/use-feature-flags';
import { useIsLargeScreen } from '@/hooks/use-is-large-screen';
import { usePermissions } from '@/hooks/use-permissions';
import type { CalendarEvent, Task } from '@/types/models';
import { TaskStatus } from '@/types/models';
import { bucketTasksByDueDay, daysOverdue } from '@/utils/task-due-buckets';

const STATUS_DOT_COLORS: Record<string, string> = {
	[TaskStatus.Open]: '#1565c0',
	[TaskStatus.InProgress]: '#e65100',
	[TaskStatus.Completed]: '#2e7d32',
	[TaskStatus.Cancelled]: '#757575',
	[TaskStatus.Archived]: '#ad1457',
};

const MAX_ITEMS_PER_SECTION = 5;

const formatTime = (timestamp: number): string => {
	return new Date(timestamp).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
};

const formatOverdue = (dueDate: unknown): string => {
	const days = daysOverdue(dueDate) ?? 0;
	if (days <= 0) return 'Today';
	if (days === 1) return '1d ago';
	return `${days}d ago`;
};

const hasSpecificTime = (task: Task): boolean => {
	return !!(task.startTime || task.endTime);
};

const CardHeader: React.FC<{
	label: string;
	count: number;
	color?: string;
}> = ({ label, count, color }) => {
	const theme = useTheme();
	const accentColor = color || theme.colors.primary;
	return (
		<View style={{ flexDirection: 'row', alignItems: 'center', gap: 10, marginBottom: 4 }}>
			<Text style={{ fontSize: 18, fontWeight: '700', color: theme.colors.onSurface }}>{label}</Text>
			<View style={{
				backgroundColor: accentColor,
				borderRadius: 10,
				minWidth: 22,
				height: 22,
				alignItems: 'center',
				justifyContent: 'center',
				paddingHorizontal: 7,
			}}>
				<Text style={{ fontSize: 12, fontWeight: '700', color: '#fff' }}>{count}</Text>
			</View>
		</View>
	);
};

const SectionHeader: React.FC<{
	icon: keyof typeof Ionicons.glyphMap;
	label: string;
	count: number;
	color?: string;
}> = ({ icon, label, count, color }) => {
	const theme = useTheme();
	const accentColor = color || theme.colors.primary;
	return (
		<View style={{ flexDirection: 'row', alignItems: 'center', gap: 8, marginTop: 12, marginBottom: 6 }}>
			<Ionicons name={icon} size={16} color={accentColor} />
			<Text style={{ fontSize: 13, fontWeight: '600', color: theme.colors.onSurface }}>{label}</Text>
			<View style={{
				backgroundColor: accentColor,
				borderRadius: 10,
				minWidth: 20,
				height: 20,
				alignItems: 'center',
				justifyContent: 'center',
				paddingHorizontal: 6,
			}}>
				<Text style={{ fontSize: 11, fontWeight: '700', color: '#fff' }}>{count}</Text>
			</View>
		</View>
	);
};

const CompactTaskRow: React.FC<{
	task: Task;
	isOverdue?: boolean;
	index: number;
	onPress: (task: Task) => void;
}> = ({ task, isOverdue, index, onPress }) => {
	const theme = useTheme();
	const dotColor = STATUS_DOT_COLORS[task.status] || theme.colors.primary;

	const timeLabel = isOverdue
		? formatOverdue(task.dueDate)
		: hasSpecificTime(task) ? formatTime((task.startTime || task.endTime)!) : undefined;

	const stripeColor = index % 2 === 0 ? 'rgba(128,128,128,0.06)' : theme.colors.surfaceVariant;

	const isWeb = Platform.OS === 'web';

	return (
		<Pressable
			onPress={() => onPress(task)}
			style={({ hovered }: any) => ({
				flexDirection: 'row' as const,
				alignItems: 'center' as const,
				paddingVertical: 8,
				paddingHorizontal: 8,
				gap: 10,
				backgroundColor: hovered && isWeb ? `${theme.colors.primary}18` : stripeColor,
				...(hovered && isWeb ? {
					transform: [{ translateY: -1 }],
					shadowColor: '#000',
					shadowOffset: { width: 0, height: 2 },
					shadowOpacity: 0.12,
					shadowRadius: 4,
					elevation: 3,
				} : {}),
				cursor: isWeb ? 'pointer' : undefined,
			} as any)}
		>
			<View style={{
				width: 8,
				height: 8,
				borderRadius: 4,
				backgroundColor: dotColor,
			}} />
			<Text
				numberOfLines={1}
				style={{ flex: 1, fontSize: 14, color: theme.colors.onSurface }}
			>
				{task.title}
			</Text>
			{timeLabel ? (
				<Text style={{
					fontSize: 12,
					color: isOverdue ? '#d32f2f' : theme.colors.onSurfaceVariant,
					fontWeight: isOverdue ? '600' : '400',
				}}>
					{timeLabel}
				</Text>
			) : null}
			<Ionicons name="chevron-forward" size={14} color={theme.colors.onSurfaceVariant} />
		</Pressable>
	);
};

const CompactEventRow: React.FC<{ event: CalendarEvent }> = ({ event }) => {
	const theme = useTheme();
	const dotColor = event.color || '#4285f4';

	const timeLabel = event.isAllDay
		? 'All Day'
		: `${formatTime(event.startTime)} - ${formatTime(event.endTime)}`;

	const handlePress = () => {
		if (event.htmlLink) {
			Linking.openURL(event.htmlLink);
		}
	};

	return (
		<TouchableOpacity
			onPress={handlePress}
			disabled={!event.htmlLink}
			style={{
				flexDirection: 'row',
				alignItems: 'center',
				paddingVertical: 8,
				paddingHorizontal: 4,
				gap: 10,
			}}
		>
			<View style={{
				width: 8,
				height: 8,
				borderRadius: 4,
				backgroundColor: dotColor,
			}} />
			<Text style={{ fontSize: 12, color: theme.colors.onSurfaceVariant, minWidth: 90 }}>
				{timeLabel}
			</Text>
			<Text
				numberOfLines={1}
				style={{ flex: 1, fontSize: 14, color: theme.colors.onSurface }}
			>
				{event.title}
			</Text>
			{event.htmlLink ? (
				<Ionicons name="open-outline" size={14} color={theme.colors.onSurfaceVariant} />
			) : null}
		</TouchableOpacity>
	);
};

export const TodaySection: React.FC<{ style?: ViewStyle }> = ({ style }) => {
	const { hasFlag } = useFeatureFlags();
	const { user } = useApi();
	const theme = useTheme();
	const isLargeScreen = useIsLargeScreen();
	const [selectedTask, setSelectedTask] = useState<Task | null>(null);

	// Viewer-local day bounds — for calendar EVENTS only, which are real moments. Tasks are bucketed
	// by their due business day against ET today (bucketTasksByDueDay).
	const { startOfToday, endOfToday } = useMemo(() => {
		const d = new Date();
		const start = new Date(d.getFullYear(), d.getMonth(), d.getDate()).getTime();
		return { startOfToday: start, endOfToday: start + 86400000 };
	}, []);

	const tasksEnabled = hasFlag('tasks_enabled');
	const { enabled: dashboardTasksPref } = useDashboardTasks();
	const { isContractor } = usePermissions();
	const showSection = tasksEnabled && dashboardTasksPref && !isContractor;
	const userId = user?.id;

	const { data: tasksData, isLoading: tasksLoading } = useTasks(
		showSection && userId
			? { userScope: userId, limit: 200 }
			: { enabled: false },
	);

	const { data: eventsData, isLoading: eventsLoading } = useCalendarEvents(
		showSection ? startOfToday : 0,
		showSection ? endOfToday : 0,
	);

	const { overdueTasks, todayTasks } = useMemo(() => {
		const tasks = tasksData?.data ?? [];
		const active = tasks.filter(t =>
			t.status === TaskStatus.Open || t.status === TaskStatus.InProgress,
		);
		const { overdue, today } = bucketTasksByDueDay(active);
		return { overdueTasks: overdue, todayTasks: today };
	}, [tasksData]);

	const events = useMemo(() => {
		return (eventsData ?? [])
			.filter(e => e.status !== 'cancelled')
			.sort((a, b) => {
				if (a.isAllDay && !b.isAllDay) return -1;
				if (!a.isAllDay && b.isAllDay) return 1;
				return a.startTime - b.startTime;
			});
	}, [eventsData]);

	if (!showSection || !userId) return null;

	const isLoading = tasksLoading || eventsLoading;
	const isEmpty = overdueTasks.length === 0 && todayTasks.length === 0 && events.length === 0;

	if (isEmpty && !isLoading) return null;

	const handleTaskPress = (task: Task) => {
		if (isLargeScreen) {
			setSelectedTask(task);
		} else {
			router.push({ pathname: '/tasks/[id]', params: { id: task.id } });
		}
	};

	const hasBothTaskGroups = todayTasks.length > 0 && overdueTasks.length > 0;
	const sideBySide = isLargeScreen && hasBothTaskGroups;

	return (
		<>
			<View style={style}>
				{isLoading ? (
					<GlassCard>
						<ActivityIndicator size="small" color={theme.colors.primary} style={{ marginVertical: 16 }} />
					</GlassCard>
				) : (
					<>
						{(todayTasks.length > 0 || overdueTasks.length > 0) && (
							<View style={sideBySide ? { flexDirection: 'row', gap: 16 } : undefined}>
								{todayTasks.length > 0 && (
									<GlassCard style={sideBySide ? { flex: 1 } : undefined}>
										<CardHeader label="Today" count={todayTasks.length} />
										{todayTasks.slice(0, MAX_ITEMS_PER_SECTION).map((task, i) => (
											<CompactTaskRow key={task.id} task={task} index={i} onPress={handleTaskPress} />
										))}
										{todayTasks.length > MAX_ITEMS_PER_SECTION && (
											<TouchableOpacity
												onPress={() => router.push('/tasks')}
												style={{ paddingVertical: 6, paddingHorizontal: 4 }}
											>
												<Text style={{ fontSize: 13, color: theme.colors.primary, fontWeight: '500' }}>
													View all {todayTasks.length} tasks due today
												</Text>
											</TouchableOpacity>
										)}
									</GlassCard>
								)}

								{overdueTasks.length > 0 && (
									<GlassCard style={sideBySide ? { flex: 1 } : { marginTop: todayTasks.length > 0 ? 12 : 0 }}>
										<CardHeader label="Overdue" count={overdueTasks.length} color="#d32f2f" />
										{overdueTasks.slice(0, MAX_ITEMS_PER_SECTION).map((task, i) => (
											<CompactTaskRow key={task.id} task={task} index={i} isOverdue onPress={handleTaskPress} />
										))}
										{overdueTasks.length > MAX_ITEMS_PER_SECTION && (
											<TouchableOpacity
												onPress={() => router.push('/tasks')}
												style={{ paddingVertical: 6, paddingHorizontal: 4 }}
											>
												<Text style={{ fontSize: 13, color: theme.colors.primary, fontWeight: '500' }}>
													View all {overdueTasks.length} overdue tasks
												</Text>
											</TouchableOpacity>
										)}
									</GlassCard>
								)}
							</View>
						)}

						{events.length > 0 && (
							<GlassCard style={{ marginTop: (todayTasks.length > 0 || overdueTasks.length > 0) ? 12 : 0 }}>
								<SectionHeader icon="calendar-outline" label="Today's Schedule" count={events.length} />
								{events.slice(0, MAX_ITEMS_PER_SECTION).map(event => (
									<CompactEventRow key={event.id} event={event} />
								))}
								{events.length > MAX_ITEMS_PER_SECTION && (
									<Text style={{ fontSize: 13, color: theme.colors.onSurfaceVariant, paddingVertical: 6, paddingHorizontal: 4 }}>
										+{events.length - MAX_ITEMS_PER_SECTION} more events
									</Text>
								)}
							</GlassCard>
						)}
					</>
				)}
			</View>

			<TaskDetailModal task={selectedTask} onDismiss={() => setSelectedTask(null)} />
		</>
	);
};
